import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  OutlinedInput,
  Select,
  MenuItem,
} from "~/components/UI";
import Dialog, { DialogProps } from "~/components/UI/Dialog/Dialog";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import yup from "~/packages/yup";
import api from "~/api";
import { EvPortType } from "~/api/evports";

interface UpdateEvPortProps extends Omit<DialogProps, "onClose"> {
  evPort?: Api.EvPortDto;
  onClose?: (result?: boolean) => void;
}

const defaultValues = {
  portType: "" as any,
  id: "",
  articleCode: "",
  articleName: ""
} as Api.CreateEvPort | Api.UpdateEvPort;

/**
 * @memberof EvPorts
 * @component
 * @desc Dialog for creating or editing an evPort.
 * @property {Api.EvPortDto} evPort
 * @property {Function} onClose - passes true if the ev port was created/updated successfully. (result?: boolean) => void;
 */
const UpdateEvPortDialog = ({ evPort, open, onClose }: UpdateEvPortProps) => {
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [isEditMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const validationSchema = yup.object().shape({
    portType: yup.string().required(t("validation.required")),
    articleCode: yup.string().required(t("validation.required")),
    articleName: yup.string(),
  });

  const onSubmit = useCallback(
    async (values) => {
      setLoading(true);
      try {
        if (isEditMode && evPort?.id) {
          await api.evports.updateEvPort(evPort.id, values as Api.UpdateEvPort);
          toast.success(t("text.recordWasSuccessfullyEdited"));
        } else {
          await api.evports.createEvPort(values as Api.CreateEvPort);
          toast.success(t("text.recordWasSuccessfullyCreated"));
        }
        onClose && onClose(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [isEditMode, evPort, onClose, t]
  );

  useEffect(() => {
    if (evPort && evPort.id) {
      setInitialValues({
        portType: evPort.portType,
        articleCode: evPort.articleCode,
        articleName: evPort.articleName,
      });
      setEditMode(true);
    } else {
      setInitialValues(defaultValues);
      setEditMode(false);
    }
  }, [evPort]);

  return (
    <Dialog
      open={open}
      title={isEditMode ? t("title.editEvPort") : t("title.createNewEvPort")}
      onClose={() => !loading && onClose && onClose()}
      closable
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ values, handleSubmit }) => (
          <>
            <DialogContent>
              <Box mb={3}>
                <FormControlLabel label={t("label.portType")}>
                  <Select
                    name="portType"
                    formikControll
                    fullWidth
                    disabled={isEditMode}
                  >
                    {Object.values(EvPortType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControlLabel>
                <FormControlLabel label={t("label.articleCode")}>
                  <OutlinedInput name="articleCode" formikControll fullWidth />
                </FormControlLabel>
                <FormControlLabel label={t("label.articleName")}>
                  <OutlinedInput
                    name="articleName"
                    formikControll
                    fullWidth
                    placeholder={t("placeholder.optional")}
                  />
                </FormControlLabel>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                color="primary"
                variant="contained"
                loading={loading}
                onClick={() => handleSubmit()}
              >
                {isEditMode ? t("button.save") : t("button.create")}
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => onClose && onClose()}
              >
                {t("button.cancel")}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default UpdateEvPortDialog;
